/**
 * Birthday Sale PDP card: inactive→active toggle, tiered/flat %, selector + CTA prices,
 * site-wide session activation, and cart-code apply after ATC.
 */
(function () {
  'use strict';

  var ACTIVE_KEY = 'birthdaySale:active';
  var DISMISSED_KEY = 'birthdaySale:codeDismissed';
  var TIER_THRESHOLD_DEFAULT = 99;
  var saveWord = '';

  function formatMoney(amount) {
    if (amount == null || isNaN(amount)) return '';
    return Number(amount).toFixed(2) + ' €';
  }

  /** Birthday card + ATC: trailing euro with dot decimal (e.g. 49.99 €). */
  function formatCardPrice(amount) {
    return formatMoney(amount);
  }

  function parseMoney(value) {
    if (value == null || value === '') return null;
    if (typeof value === 'number' && !isNaN(value)) return value;
    var cleaned = String(value).replace(/[^\d,.-]/g, '');
    if (!cleaned) return null;
    if (cleaned.indexOf(',') > -1 && cleaned.indexOf('.') > -1) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (cleaned.indexOf(',') > -1) {
      cleaned = cleaned.replace(',', '.');
    }
    var amount = parseFloat(cleaned);
    return isNaN(amount) ? null : amount;
  }

  function eurosToCents(amount) {
    if (amount == null || isNaN(amount)) return 0;
    return Math.round(Number(amount) * 100);
  }

  function centsToEuros(cents) {
    return Number(cents) / 100;
  }

  /** Match Shopify line-item rounding (integer cents). */
  /** Match Shopify line-item % discounts: price − floor(price × percent / 100). */
  function discountCents(baseCents, percent) {
    baseCents = parseInt(baseCents, 10) || 0;
    percent = parseFloat(percent) || 0;
    if (!baseCents || !percent) return baseCents;
    var offCents = Math.floor((baseCents * percent) / 100);
    return baseCents - offCents;
  }

  function readSelectedVariantPriceCents(root) {
    var el = productRoot(root).querySelector('variant-selects [data-selected-variant]');
    if (!el) return null;
    try {
      var variant = JSON.parse(el.textContent);
      if (variant && variant.price != null) return parseInt(variant.price, 10);
    } catch (e) {}
    return null;
  }

  function readPackagePriceCents(node) {
    if (!node) return null;
    var priceNodes = node.querySelectorAll('[itemprop="price"]');
    var totalNode = priceNodes.length ? priceNodes[priceNodes.length - 1] : null;
    if (totalNode) {
      var contentCents = parseInt(totalNode.getAttribute('content'), 10);
      if (!isNaN(contentCents) && contentCents > 0) return contentCents;
    }
    var baseEuros = readBasePackagePrice(node);
    return baseEuros != null ? eurosToCents(baseEuros) : null;
  }

  function isActive() {
    try {
      return sessionStorage.getItem(ACTIVE_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function isCodeDismissed() {
    try {
      return sessionStorage.getItem(DISMISSED_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setCodeDismissed(on) {
    try {
      if (on) sessionStorage.setItem(DISMISSED_KEY, '1');
      else sessionStorage.removeItem(DISMISSED_KEY);
    } catch (e) {}
  }

  function setActive(on) {
    try {
      if (on) {
        sessionStorage.setItem(ACTIVE_KEY, '1');
        sessionStorage.removeItem(DISMISSED_KEY);
      } else {
        sessionStorage.removeItem(ACTIVE_KEY);
      }
    } catch (e) {}
    document.documentElement.toggleAttribute('data-birthday-sale-active', !!on);
  }

  function isSegmentDiscountCode(code, content) {
    var normalized = normalizeCode(code);
    if (!normalized) return false;
    content = content || getContent() || {};
    if (
      segmentDiscountCodes(content).some(function (entry) {
        return normalizeCode(entry) === normalized;
      })
    ) {
      return true;
    }
    // Birthday campaign codes (flat / tiered / CRM / Meta) even if segment content is stale.
    return /^(BIRTHDAY\d*|BDAY[A-Z0-9]*|MYBACS\d*|VIPBDAY\d*)$/i.test(normalized);
  }

  function clearDiscountCodeCookie() {
    try {
      var expires = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
      document.cookie = 'discount_code=; path=/; ' + expires;
      document.cookie = 'discount_code=; path=/; domain=' + location.hostname + '; ' + expires;
      var parts = location.hostname.split('.');
      if (parts.length > 2) {
        document.cookie =
          'discount_code=; path=/; domain=.' + parts.slice(-2).join('.') + '; ' + expires;
      }
    } catch (e) {}
  }

  /** User removed birthday from cart or chose another code — stop auto-reapply. */
  function dismissBirthdayCode() {
    applyCodeToCart._gen = (applyCodeToCart._gen || 0) + 1;
    applyCodeToCart._pending = null;
    setCodeDismissed(true);
    setActive(false);
    clearDiscountCodeCookie();
    try {
      sessionStorage.removeItem('cart-checkout-discount-codes');
    } catch (e) {}
    document.querySelectorAll('birthday-sale-card').forEach(function (card) {
      card.classList.remove('is-active');
      card.classList.add('is-toggle');
      var toggle = card.querySelector('[data-birthday-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-checked', 'false');
        toggle.setAttribute('aria-pressed', 'false');
      }
      var idle = card.querySelector('[data-birthday-cta-idle]');
      var on = card.querySelector('[data-birthday-cta-on]');
      if (idle) idle.classList.remove('hidden');
      if (on) on.classList.add('hidden');
    });
  }

  function handleCartCodeRemoved(code) {
    // Cart UI often passes discount.title (not the raw code). If birthday is active,
    // any manual removal must stop auto-reapply — matching by code alone is unreliable.
    if (isActive() || isSegmentDiscountCode(code)) dismissBirthdayCode();
  }

  function handleCartCodeAdded(code) {
    if (!code) return;
    if (isSegmentDiscountCode(code)) {
      setCodeDismissed(false);
      return;
    }
    // Non-birthday code takes priority over birthday auto-apply.
    dismissBirthdayCode();
  }

  /** True when cart already has a discount that is not this campaign's birthday code. */
  function cartHasForeignDiscountCode(cart) {
    if (!cart) return false;
    var titles = [];
    (cart.cart_level_discount_applications || []).forEach(function (app) {
      if (app && (app.title || app.code)) titles.push(app.title || app.code);
    });
    (cart.discount_codes || []).forEach(function (entry) {
      if (!entry) return;
      if (typeof entry === 'string') titles.push(entry);
      else if (entry.code) titles.push(entry.code);
    });
    if (!titles.length) return false;
    return titles.some(function (title) {
      return !isSegmentDiscountCode(title);
    });
  }

  function getContent() {
    if (!window.CampaignSegmentation) return null;
    return window.CampaignSegmentation.getContent() || null;
  }

  function getUrlParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (e) {
      return null;
    }
  }

  function readUrlDiscountCode() {
    var fromQuery = getUrlParam('discount') || getUrlParam('discount_code') || getUrlParam('discountCode');
    if (fromQuery) return String(fromQuery).trim();

    var pathMatch = window.location.pathname.match(/\/discount\/([^/?#]+)/i);
    if (pathMatch && pathMatch[1]) return decodeURIComponent(pathMatch[1]).trim();

    try {
      var cookieMatch = document.cookie.match(/(?:^|; )discount_code=([^;]*)/);
      if (cookieMatch && cookieMatch[1]) return decodeURIComponent(cookieMatch[1]).trim();
    } catch (e) {}

    return '';
  }

  function normalizeCode(code) {
    return String(code || '')
      .trim()
      .toUpperCase();
  }

  function segmentDiscountCodes(content) {
    content = content || {};
    var codes = [];
    if (content.discount_code) codes.push(content.discount_code);
    if (content.tier_discount_code_low) codes.push(content.tier_discount_code_low);
    if (content.tier_discount_code_high) codes.push(content.tier_discount_code_high);
    return codes;
  }

  function codeMatchesSegment(urlCode, content) {
    if (!urlCode) return false;
    var normalized = normalizeCode(urlCode);
    return segmentDiscountCodes(content).some(function (code) {
      return normalizeCode(code) === normalized;
    });
  }

  function maybeAutoActivateBirthday(content) {
    content = content || getContent() || {};
    if (isCodeDismissed()) return false;
    if (isInfoCardMode(content)) return false;
    if (!segmentDiscountCodes(content).length && content.discount_mode !== 'tiered') return false;

    var fromSegment = !!content.birthday_auto_activate;
    var urlCode = readUrlDiscountCode();
    var fromUrl = urlCode && codeMatchesSegment(urlCode, content);

    if (!fromSegment && !fromUrl) return false;

    setActive(true);
    return true;
  }

  /** @deprecated use maybeAutoActivateBirthday */
  function maybeAutoActivateFromUrl(content) {
    return maybeAutoActivateBirthday(content);
  }

  function finishAutoActivate() {
    syncAllCards();
    if (!isActive() || isInfoCardMode()) return;
    fetchCart().then(function (cart) {
      applyCodeForActiveState(cart, selectedTotals().packagePrice);
    });
  }

  function resolvePercent(content, basketEuros) {
    if (!content) return 0;
    if (content.discount_mode === 'tiered') {
      var threshold = parseFloat(content.tier_threshold) || TIER_THRESHOLD_DEFAULT;
      var low = parseFloat(content.tier_percent_low) || 25;
      var high = parseFloat(content.tier_percent_high) || 30;
      return basketEuros >= threshold ? high : low;
    }
    return parseFloat(content.discount_percent) || 0;
  }

  function displayPercentForCard(content, appliedPercent) {
    content = content || {};
    if (content.discount_mode === 'tiered') {
      var high = parseFloat(content.tier_percent_high);
      if (!isNaN(high) && high > 0) return high;
    }
    return appliedPercent || 0;
  }

  function lineSubtotalEuros(item) {
    if (!item) return 0;
    if (item.original_line_price != null) return item.original_line_price / 100;
    if (item.line_price != null) return item.line_price / 100;
    return ((item.price || 0) * (item.quantity || 1)) / 100;
  }

  function cartMerchandiseSubtotalEuros(cart) {
    if (!cart) return 0;
    if (Array.isArray(cart.items) && cart.items.length) {
      return cart.items.reduce(function (sum, item) {
        return sum + lineSubtotalEuros(item);
      }, 0);
    }
    if (cart.items_subtotal_price != null) return cart.items_subtotal_price / 100;
    if (cart.original_total_price != null) return cart.original_total_price / 100;
    return (cart.total_price || 0) / 100;
  }

  function projectedBasketForVariant(cart, variantPriceEuros, root) {
    variantPriceEuros = variantPriceEuros || 0;
    if (!cart || !Array.isArray(cart.items) || !cart.items.length) {
      return variantPriceEuros;
    }

    var pid = productId(root);
    var otherEuros = 0;
    var foundProduct = false;

    cart.items.forEach(function (item) {
      if (pid && String(item.product_id) === String(pid)) {
        foundProduct = true;
      } else {
        otherEuros += lineSubtotalEuros(item);
      }
    });

    if (foundProduct) return otherEuros + variantPriceEuros;
    return cartMerchandiseSubtotalEuros(cart) + variantPriceEuros;
  }

  function resolvePercentForVariant(content, cart, variantPriceEuros, root) {
    return resolvePercent(content, projectedBasketForVariant(cart, variantPriceEuros, root));
  }

  function optionNodeFromTag(tag) {
    return (
      tag.closest('.option__info') ||
      tag.closest('.v2-option-pill') ||
      tag.closest('.dropdown-option') ||
      tag.closest('.september-card') ||
      tag.closest('.menobacs-card') ||
      null
    );
  }

  function readOptionVariantPriceFromDom(node) {
    if (!node) return null;
    if (node.dataset.priceDefault && !node.dataset.birthdayBaseTotal && !node.dataset.campaignBaseTotal) {
      var fromData = parseMoney(node.dataset.priceDefault);
      if (fromData != null) return fromData;
    }
    var priceNodes = node.querySelectorAll('[itemprop="price"]');
    var totalNode = priceNodes.length ? priceNodes[priceNodes.length - 1] : null;
    if (totalNode) {
      var cents = parseFloat(totalNode.getAttribute('content'));
      if (!isNaN(cents)) return cents / 100;
      var parsed = parseMoney(String(totalNode.textContent).split('/')[0]);
      if (parsed != null) return parsed;
    }
    var h5 = node.querySelector('.variant-price .h5[data-variant], .variant-price > span.h5, .variant-price > .h5');
    if (h5) {
      var strikeSpans = h5.querySelectorAll('span');
      if (strikeSpans.length) {
        var last = parseMoney(strikeSpans[strikeSpans.length - 1].textContent);
        if (last != null) return last;
      }
      var fromH5 = parseMoney(h5.textContent);
      if (fromH5 != null) return fromH5;
    }
    return null;
  }

  function readOptionPerDayFromDom(node) {
    if (!node) return null;
    var dayEl = node.querySelector('.variant-price [data-nosnippet]');
    if (dayEl) {
      var dayMatch = String(dayEl.textContent).match(/([\d.,]+)/);
      if (dayMatch) return parseMoney(dayMatch[1]);
    }
    var priceNodes = node.querySelectorAll('[itemprop="price"]');
    if (priceNodes.length > 1) {
      var cents = parseFloat(priceNodes[0].getAttribute('content'));
      if (!isNaN(cents)) return cents / 100;
    }
    return null;
  }

  function readBasePackagePrice(node) {
    if (!node) return null;
    var fromStored =
      parseMoney(node.dataset.birthdayBaseTotal) || parseMoney(node.dataset.campaignBaseTotal);
    if (fromStored != null) return fromStored;
    if (node.dataset.priceDefault) {
      var fromData = parseMoney(node.dataset.priceDefault);
      if (fromData != null) return fromData;
    }
    return readOptionVariantPriceFromDom(node);
  }

  function readBasePerDay(node) {
    if (!node) return null;
    var fromStored = parseMoney(node.dataset.birthdayBasePerDay);
    if (fromStored != null) return fromStored;
    if (node.dataset.price) {
      var fromData = parseMoney(node.dataset.price);
      if (fromData != null) return fromData;
    }
    return readOptionPerDayFromDom(node);
  }

  function resetLegacyOptionBirthdayCache(root) {
    productRoot(root).querySelectorAll('.option__info').forEach(function (el) {
      delete el.dataset.birthdayBaseTotal;
      delete el.dataset.birthdayBasePerDay;
      delete el.dataset.birthdayPriceHtml;
    });
  }

  function readOptionVariantPrice(node) {
    if (!node) return 0;
    if (node.dataset.birthdayBaseTotal) {
      var remembered = parseMoney(node.dataset.birthdayBaseTotal);
      if (remembered != null) return remembered;
    }
    if (node.dataset.priceDefault) {
      var fromData = parseMoney(node.dataset.priceDefault);
      if (fromData != null) return fromData;
    }
    var priceNodes = node.querySelectorAll('[itemprop="price"]');
    var totalNode = priceNodes.length ? priceNodes[priceNodes.length - 1] : null;
    if (totalNode) {
      var cents = parseFloat(totalNode.getAttribute('content'));
      if (!isNaN(cents)) return cents / 100;
      var parsed = parseMoney(String(totalNode.textContent).split('/')[0]);
      if (parsed != null) return parsed;
    }
    var h5 = node.querySelector('.variant-price .h5[data-variant], .variant-price > span.h5, .variant-price > .h5');
    if (h5) {
      var strikeSpans = h5.querySelectorAll('span');
      if (strikeSpans.length) {
        var last = parseMoney(strikeSpans[strikeSpans.length - 1].textContent);
        if (last != null) return last;
      }
      var fromH5 = parseMoney(h5.textContent);
      if (fromH5 != null) return fromH5;
    }
    return 0;
  }

  function readOptionPerDay(node) {
    if (!node) return null;
    if (node.dataset.birthdayBasePerDay) return parseMoney(node.dataset.birthdayBasePerDay);
    var dayEl = node.querySelector('.variant-price [data-nosnippet]');
    if (dayEl) {
      var dayMatch = String(dayEl.textContent).match(/([\d.,]+)/);
      if (dayMatch) return parseMoney(dayMatch[1]);
    }
    var priceNodes = node.querySelectorAll('[itemprop="price"]');
    if (priceNodes.length > 1) {
      var cents = parseFloat(priceNodes[0].getAttribute('content'));
      if (!isNaN(cents)) return cents / 100;
    }
    return null;
  }

  function formatSelectorTagText(percent) {
    if (!percent) return '';
    return '-' + percent + '%';
  }

  function productRoot(fromEl) {
    if (fromEl && fromEl.nodeType === 1) {
      if (fromEl.matches && fromEl.matches('product-info')) return fromEl;
      var scoped =
        fromEl.closest('product-info') ||
        fromEl.closest('quick-add-modal') ||
        fromEl.closest('product-form');
      if (scoped) return scoped;
    }
    return (
      document.querySelector('product-info') ||
      document.querySelector('product-form') ||
      document.querySelector('.product') ||
      document
    );
  }

  function isQuickAddRoot(root) {
    return !!(root && root.closest && root.closest('quick-add-modal'));
  }

  function variantEventMatchesCard(event, root) {
    var sectionId = event && event.data && event.data.sectionId;
    if (!sectionId) return true;
    var ctx = productRoot(root);
    var info =
      (ctx.matches && ctx.matches('product-info') && ctx) ||
      (ctx.querySelector && ctx.querySelector('product-info'));
    var rootSection = info && (info.dataset.section || info.getAttribute('data-section'));
    if (!rootSection) return true;
    return String(sectionId) === String(rootSection);
  }

  function pickerPriceNodesSelector() {
    return '.v2-option-pill[data-price-default], .dropdown-option[data-price-default], .menobacs-card[data-price-default], .september-card[data-price-default]';
  }

  function activePill(root) {
    var ctx = productRoot(root);
    return (
      ctx.querySelector('.september-card.active') ||
      ctx.querySelector('.menobacs-card.active') ||
      ctx.querySelector('.v2-option-pill.active') ||
      ctx.querySelector('.dropdown-option.selected') ||
      null
    );
  }

  function isSubscription(root) {
    var ctx = productRoot(root);
    var picker = ctx.querySelector('plan-picker-september, plan-picker-v2-ab, plan-picker-menobacs, .plan-picker');
    var type = picker && (picker.activeType || picker.dataset.currentType);
    if (type) return type === 'subscription';
    var sellingPlan = ctx.querySelector('product-form input[name="selling_plan"], input[name="selling_plan"]');
    return !!(sellingPlan && !sellingPlan.hasAttribute('disabled'));
  }

  function orangePercent(pill) {
    if (!pill) return 0;
    var raw = pill.dataset.discount || pill.getAttribute('data-discount') || '';
    if (!raw || String(raw).replace(/[^\d]/g, '') === '0') {
      // Orange plan badge only — never the purple campaign tag.
      var badge =
        pill.querySelector('.september-card__discount.variant-discount-v1[data-discount]') ||
        pill.querySelector('.menobacs-card__discount.variant-discount-v1[data-discount]') ||
        pill.querySelector('.variant-discount-v1[data-discount]:not(.variant-discount-campaign)') ||
        pill.querySelector('.v2-pill-discount[data-discount]') ||
        pill.querySelector('.variant-discount-v1:not(.variant-discount-campaign)') ||
        pill.querySelector('.v2-pill-discount') ||
        pill.querySelector('.september-card__discount') ||
        pill.querySelector('.menobacs-card__discount');
      if (badge && badge.offsetParent === null && badge.closest && badge.closest('.hidden, [hidden]')) {
        badge = null;
      }
      raw =
        (badge && (badge.dataset.discount || badge.getAttribute('data-discount') || badge.textContent)) ||
        '';
    }
    var match = String(raw).match(/(\d+)/);
    return match ? parseFloat(match[1]) : 0;
  }

  function activePackageInfo(root) {
    // Legacy radio/label picker (e.g. dietbacs-new / product-option-v1).
    var ctx = productRoot(root);
    var radio = ctx.querySelector('.product-options_package input[type="radio"]:checked');
    if (!radio) return null;
    var wrapper =
      (radio.id && ctx.querySelector('label[for="' + radio.id + '"]')) ||
      radio.closest('label') ||
      radio.closest('.product-options_package-item, .product-option, li, div');
    if (!wrapper) return null;
    var wantType = isSubscription(root) ? 'subscription' : 'onetimer';
    return (
      wrapper.querySelector('.option__info[data-type="' + wantType + '"]:not(.hidden)') ||
      wrapper.querySelector('.option__info[data-type="' + wantType + '"]') ||
      wrapper.querySelector('.option__info:not(.hidden)') ||
      wrapper.querySelector('.option__info')
    );
  }

  function selectedOptionNode(root) {
    var pill = activePill(root);
    if (pill) return pill;
    return activePackageInfo(root);
  }

  /** Live total + strike from the active variant picker (v2 pill or legacy option__info). */
  function readPickerDisplayedTotals(root) {
    var pill = selectedOptionNode(root);
    var sale = null;
    var strike = null;
    var perDay = null;

    if (pill) {
      sale = readOptionVariantPriceFromDom(pill);
      perDay = readOptionPerDayFromDom(pill);

      if (sale == null && pill.dataset && pill.dataset.priceDefault) {
        sale = parseMoney(pill.dataset.priceDefault);
      }

      if (pill.dataset) {
        strike = parseMoney(pill.dataset.strikePrice);
        if (strike == null) strike = parseMoney(pill.dataset.birthdayBaseStrike);
        if (strike == null) strike = parseMoney(pill.dataset.campaignBaseStrike);
      }

      if (sale == null) {
        var h5 = pill.querySelector('.option__info-price .variant-price .h5, .variant-price .h5[data-variant]');
        if (h5) {
          var spans = h5.querySelectorAll('span');
          if (spans.length > 1) {
            sale = parseMoney(spans[spans.length - 1].textContent);
            strike = parseMoney(spans[0].textContent) || strike;
          } else if (!h5.hasAttribute('data-nosnippet')) {
            sale = parseMoney(h5.textContent);
          }
        }
      }
    }

    if (sale == null) {
      var ctx = productRoot(root);
      var prefix = isSubscription(root) ? 'sub' : 'ot';
      var totalEl = ctx.querySelector('#item_' + prefix + '_total');
      if (totalEl) sale = parseMoney(totalEl.textContent);
      if (strike == null) {
        var strikeEl = ctx.querySelector('#item_' + prefix + '_strike_price');
        if (strikeEl && strikeEl.style.display !== 'none') {
          strike = parseMoney(strikeEl.textContent);
        }
      }
    }

    if (sale == null) return { pill: pill, sale: null, strike: null, perDay: perDay };
    var listStrike = resolveListStrikeEuros(
      parseMoney(pill && pill.dataset && pill.dataset.birthdayBaseTotal) || sale,
      pill
    );
    if (listStrike > sale) strike = listStrike;
    if (strike == null || strike <= sale) strike = sale;
    return { pill: pill, sale: sale, strike: strike, perDay: perDay };
  }

  function paintBirthdayCardPrices(card, content, cart, percent, active, infoMode, totals) {
    var root = productRoot(card);
    var picker = readPickerDisplayedTotals(root);
    var pill = (picker && picker.pill) || (totals && totals.pill) || selectedOptionNode(root);
    var displayCents = 0;
    var strikeAmtCents = 0;

    var preBirthday =
      parseMoney(pill && pill.dataset && pill.dataset.birthdayBaseTotal) ||
      (picker.sale != null && !active ? picker.sale : null) ||
      (totals && totals.packagePrice) ||
      null;

    if (picker.sale != null && picker.sale > 0) {
      displayCents = eurosToCents(picker.sale);
    } else if (preBirthday != null) {
      displayCents =
        active && percent > 0
          ? discountCents(eurosToCents(preBirthday), percent)
          : eurosToCents(preBirthday);
    }

    if (displayCents > 0) {
      var baseForList = preBirthday != null ? preBirthday : centsToEuros(displayCents);
      var listStrike = resolveListStrikeEuros(baseForList, pill);
      if (listStrike > centsToEuros(displayCents)) {
        strikeAmtCents = eurosToCents(listStrike);
      }
    }

    // Always show selected variant total; strikethrough = true list price (plan discount).
    var showCardPrices = displayCents > 0 && !infoMode;
    var pricesWrap = card.querySelector('[data-birthday-prices]');
    if (pricesWrap) {
      pricesWrap.hidden = !showCardPrices;
      if (showCardPrices) pricesWrap.removeAttribute('hidden');
    }

    var strikeEl = card.querySelector('[data-birthday-strike]');
    var priceEl = card.querySelector('[data-birthday-price]');
    if (strikeEl) {
      strikeEl.textContent =
        showCardPrices && strikeAmtCents > displayCents
          ? formatCardPrice(centsToEuros(strikeAmtCents))
          : '';
    }
    if (priceEl) {
      priceEl.textContent = showCardPrices ? formatCardPrice(centsToEuros(displayCents)) : '';
    }
  }

  function readAtcMainPriceCents(root) {
    var variantCents = readSelectedVariantPriceCents(root);
    if (variantCents != null && variantCents > 0) {
      var mainForBase = productRoot(root).querySelector('.atc-price_main');
      if (mainForBase && mainForBase.dataset) {
        var baseKey = isSubscription(root) ? 'birthdayBaseSubscriptionPrice' : 'birthdayBaseOnetimerPrice';
        if (!mainForBase.dataset[baseKey]) {
          mainForBase.dataset[baseKey] = formatMoney(centsToEuros(variantCents));
        }
      }
      return variantCents;
    }

    var euros = readAtcMainPrice(root);
    return euros != null ? eurosToCents(euros) : null;
  }

  function readAtcMainPrice(root) {
    var ctx = productRoot(root);
    var productForm = ctx.querySelector('product-form');
    var submitButton = productForm && productForm.querySelector('[type="submit"]');
    var main =
      (submitButton && submitButton.querySelector('.atc-price_main')) ||
      ctx.querySelector('.atc-price_main');
    if (!main) return null;

    function pickBase(key) {
      if (!main.dataset) return null;
      var baseKey = 'birthdayBase' + key.charAt(0).toUpperCase() + key.slice(1);
      var campaignKey = 'campaignBase' + key.charAt(0).toUpperCase() + key.slice(1);
      if (main.dataset[baseKey]) return parseMoney(main.dataset[baseKey]);
      if (main.dataset[campaignKey]) return parseMoney(main.dataset[campaignKey]);
      if (main.dataset[key] && !document.documentElement.hasAttribute('data-birthday-sale-active')) {
        return parseMoney(main.dataset[key]);
      }
      return null;
    }

    var sub = pickBase('subscriptionPrice');
    var ot = pickBase('onetimerPrice');
    if (isSubscription(root) && sub != null) return sub;
    if (!isSubscription(root) && ot != null) return ot;
    if (ot != null) return ot;
    if (sub != null) return sub;
    return parseMoney(main.textContent);
  }

  function readAtcCompareStrike(root) {
    var ctx = productRoot(root);
    var productForm = ctx.querySelector('product-form');
    var submitButton = productForm && productForm.querySelector('[type="submit"]');
    var wrap =
      (submitButton && submitButton.querySelector('.selected-variant-price')) ||
      ctx.querySelector('.selected-variant-price');
    var saleEl = wrap && wrap.querySelector('.atc-price_sale');
    if (!saleEl || saleEl.hasAttribute('data-birthday-atc-strike')) return null;
    return parseMoney(saleEl.textContent);
  }

  function atcPriceNodes(root) {
    var ctx = productRoot(root);
    var productForm = ctx.querySelector('product-form');
    var submitButton =
      (productForm && productForm.querySelector('[type="submit"]')) ||
      ctx.querySelector('[id^="ProductSubmitButton-"]');
    var main = submitButton && submitButton.querySelector('.atc-price_main');
    var sale = submitButton && submitButton.querySelector('.atc-price_sale');
    var wrap =
      (submitButton && submitButton.querySelector('.selected-variant-price')) ||
      (main && main.parentElement);
    return { main: main, sale: sale, wrap: wrap, submitButton: submitButton, productForm: productForm };
  }

  function paintSimpleAtcWrap(wrap, main, current, strike, showStrike) {
    if (!wrap || !main) return main;
    var datasets = {};
    if (main.dataset) {
      Object.keys(main.dataset).forEach(function (key) {
        datasets[key] = main.dataset[key];
      });
    }

    if (showStrike) {
      wrap.innerHTML =
        '<span class="atc-price_sale caption" data-birthday-atc-strike="1" style="display:inline!important;color:#fff!important;text-decoration:line-through!important;text-decoration-thickness:1px;opacity:0.5!important">' +
        formatCardPrice(strike) +
        '</span><span class="atc-price_main">' +
        formatCardPrice(current) +
        '</span>';
    } else {
      wrap.innerHTML = '<span class="atc-price_main">' + formatMoney(current) + '</span>';
    }

    var nextMain = wrap.querySelector('.atc-price_main');
    if (nextMain) {
      Object.keys(datasets).forEach(function (key) {
        nextMain.dataset[key] = datasets[key];
      });
    }
    return nextMain || main;
  }

  function selectedTotals(root) {
    var pill = selectedOptionNode(root);
    var subOrOt = readBasePackagePrice(pill);
    var perDay = readBasePerDay(pill);
    if (pill && subOrOt == null) {
      // Prefer the total/package price node when several itemprop=price exist (day + total).
      var priceNodes = pill.querySelectorAll('[itemprop="price"]');
      var priceNode = priceNodes.length ? priceNodes[priceNodes.length - 1] : null;
      var cents = priceNode && parseFloat(priceNode.getAttribute('content'));
      if (!isNaN(cents) && cents != null) subOrOt = cents / 100;
    }
    if (pill && subOrOt == null) {
      // main_price_top layouts expose package total in the leading .h5 without itemprop.
      var totalText = pill.querySelector('.variant-price .h5');
      if (totalText) subOrOt = parseMoney(totalText.textContent);
    }
    if (pill && perDay == null) {
      var dayNode = pill.querySelector('[data-nosnippet]');
      var dayMatch = dayNode && String(dayNode.textContent).match(/([\d.,]+)/);
      if (dayMatch) perDay = parseMoney(dayMatch[1]);
    }
    if (!pill || !subOrOt) {
      var ctx = productRoot(root);
      var totalEl = ctx.querySelector('#item_sub_total, #item_ot_total');
      var priceEl = ctx.querySelector('#item_sub_price, #item_ot_price');
      if (!subOrOt) subOrOt = parseMoney(totalEl && totalEl.textContent);
      if (!perDay) perDay = parseMoney(priceEl && priceEl.textContent);
    }

    var packageCents = pill ? readPackagePriceCents(pill) : null;
    if (packageCents == null) packageCents = readSelectedVariantPriceCents(root);
    if (packageCents == null) packageCents = readAtcMainPriceCents(root);
    if (packageCents == null && subOrOt != null) packageCents = eurosToCents(subOrOt);

    return {
      pill: pill,
      packagePrice: packageCents ? centsToEuros(packageCents) : 0,
      packageCents: packageCents || 0,
      perDay: perDay || 0,
      orange: orangePercent(pill),
    };
  }

  function subscriptionStrike(packagePrice, orange) {
    if (!orange || orange <= 0) return packagePrice;
    return packagePrice / (1 - orange / 100);
  }

  /**
   * Fall Sale: the strike price is the price before the sale (the plan price the customer
   * would pay today), not the full list price — Omnibus "lowest price" reference.
   */
  function fallSalePreSaleStrike() {
    var fs = window.FallSale;
    if (!fs || !fs.active || fs.strikeMode === 'list') return false;
    return !!document.querySelector('birthday-sale-card[data-fall-sale-percent]');
  }

  /** True list/compare price before plan + birthday discounts (e.g. 99.47 / 0.76 for −24%). */
  function resolveListStrikeEuros(basePackageEuros, pill) {
    if (basePackageEuros == null || !(basePackageEuros > 0)) return 0;
    if (fallSalePreSaleStrike()) return Number(basePackageEuros);
    var best = Number(basePackageEuros);
    var orange = orangePercent(pill);
    if (orange > 0) {
      var fromOrange = subscriptionStrike(basePackageEuros, orange);
      if (fromOrange > best) best = fromOrange;
    }
    if (pill && pill.dataset) {
      var stored =
        parseMoney(pill.dataset.birthdayBaseStrike) || parseMoney(pill.dataset.campaignBaseStrike);
      if (stored && stored > best) best = stored;
    }
    var fromOption = readOptionStrike(pill);
    if (fromOption && fromOption > best) best = fromOption;
    var bundled = readBundledListStrike(pill);
    if (bundled && bundled > best) best = bundled;
    return best;
  }

  function fetchCart() {
    return fetch((window.routes && routes.cart_url ? routes.cart_url : '/cart') + '.js', {
      cache: 'no-store',
    })
      .then(function (r) {
        return r.json();
      })
      .catch(function () {
        return { total_price: 0, items: [] };
      });
  }

  function productId(root) {
    var ctx = productRoot(root);
    var info =
      (ctx.matches && ctx.matches('product-info') && ctx) ||
      (ctx.querySelector && ctx.querySelector('product-info')) ||
      document.querySelector('product-info');
    return info && (info.dataset.productId || info.getAttribute('data-product-id'));
  }

  function projectedBasketEuros(cart, packagePrice, root) {
    return projectedBasketForVariant(cart, packagePrice, root);
  }

  function stripSaveWord(root) {
    if (!saveWord) {
      // Fallback to DE/EN/ES tokens (Spare / Sparen / Save / …).
      saveWord = 'Spare|Sparen|Save|Ahorra|Risparmia';
    }
    var re = new RegExp('(' + saveWord + ')\\s*', 'gi');
    productRoot(root).querySelectorAll('.v2-pill-discount, .variant-discount-v1, .plan-button-discount-badge, .discount-badge').forEach(function (el) {
      if (el.hasAttribute('data-campaign-selector-tag')) return;
      if (el.classList.contains('variant-discount-campaign')) return;
      // September A/B keeps "Spare 10%" on the orange tag.
      if (
        (el.classList.contains('september-card__discount') && !el.closest('.plan-picker-september--es')) ||
        el.classList.contains('simplified-v1__badge')
      ) {
        if (el.dataset.birthdayOriginalText) {
          el.textContent = el.dataset.birthdayOriginalText;
          delete el.dataset.birthdaySaveStripped;
        }
        return;
      }
      if (el.dataset.birthdaySaveStripped === '1') return;
      var text = (el.textContent || '').trim();
      var next = text.replace(re, '').replace(/\s+/g, ' ').trim();
      // Ensure leading "-" on bare "10%" / "24%" forms.
      if (/^\d+\s*%$/.test(next)) next = '-' + next.replace(/\s+/g, '');
      if (next && next !== text) {
        el.dataset.birthdaySaveStripped = '1';
        el.dataset.birthdayOriginalText = text;
        el.textContent = next;
      }
    });
  }

  function rememberPillBases(root) {
    productRoot(root).querySelectorAll(pickerPriceNodesSelector()).forEach(function (el) {
      if (el.dataset.birthdayBaseTotal) return;
      el.dataset.birthdayBaseTotal = el.dataset.campaignBaseTotal || el.dataset.priceDefault || '';
      el.dataset.birthdayBasePerDay = el.dataset.price || '';
      var strike = el.dataset.campaignBaseStrike || el.dataset.strikePrice || '';
      var base = parseMoney(el.dataset.birthdayBaseTotal);
      var orange = orangePercent(el);
      if ((!parseMoney(strike) || parseMoney(strike) <= (base || 0)) && base != null && orange > 0) {
        strike = formatMoney(subscriptionStrike(base, orange));
      }
      el.dataset.birthdayBaseStrike = strike;
    });
  }

  function paintMenobacsCardPrices(el) {
    if (!el || (!el.classList.contains('menobacs-card') && !el.classList.contains('september-card'))) return;

    var total = parseMoney(el.dataset.priceDefault);
    var day = parseMoney(el.dataset.price);
    var strike = parseMoney(el.dataset.strikePrice);
    var isSeptember = el.classList.contains('september-card');
    var priceEl = el.querySelector(isSeptember ? '.september-card__price' : '.menobacs-card__price');
    var totalEl = el.querySelector(isSeptember ? '.september-card__price-total' : '.menobacs-card__price-total');
    var strikeEl = el.querySelector('.menobacs-card__strike');

    if (priceEl) {
      if (isSeptember || priceEl.hasAttribute('data-price-monthly')) {
        if (day != null) priceEl.textContent = formatMoney(day);
      } else {
        var suffixMatch = String(priceEl.textContent || '').match(/(\/.*)$/);
        var suffix = suffixMatch ? suffixMatch[1] : '';
        if (suffix && day != null) {
          priceEl.textContent = formatMoney(day) + suffix;
        } else if (!suffix && total != null) {
          priceEl.textContent = formatMoney(total);
        } else if (day != null) {
          priceEl.textContent = formatMoney(day);
        }
      }
    }

    if (totalEl && total != null) {
      var totalLabel = totalEl.dataset.totalLabel || '';
      if (!totalLabel) {
        var totalSuffixMatch = String(totalEl.textContent || '').match(/\/\s*(.+)$/);
        totalLabel = totalSuffixMatch ? totalSuffixMatch[1].trim() : '';
        if (totalLabel) totalEl.dataset.totalLabel = totalLabel;
      }
      if (isSeptember) {
        var amountEl = totalEl.querySelector('.september-card__price-total-amount');
        if (amountEl) {
          amountEl.textContent = formatMoney(total);
        } else {
          totalEl.textContent = totalLabel
            ? formatMoney(total) + ' ' + totalLabel
            : formatMoney(total);
        }
      } else {
        totalEl.textContent = totalLabel
          ? formatMoney(total) + '/' + totalLabel
          : formatMoney(total);
      }
    }

    if (strikeEl) {
      if (strike != null && total != null && strike > total) {
        strikeEl.textContent = formatMoney(strike);
        strikeEl.hidden = false;
        strikeEl.style.display = '';
      }
    }
  }

  function rememberLegacyOptionBases(root) {
    productRoot(root).querySelectorAll('.option__info').forEach(function (el) {
      if (el.dataset.birthdayBaseTotal) return;
      var baseTotal = readOptionVariantPriceFromDom(el);
      if (!baseTotal) return;
      el.dataset.birthdayBaseTotal = String(baseTotal);
      var baseDay = readOptionPerDayFromDom(el);
      if (baseDay != null) el.dataset.birthdayBasePerDay = String(baseDay);
      var orange = orangePercent(el);
      if (orange > 0) {
        el.dataset.birthdayBaseStrike = String(subscriptionStrike(baseTotal, orange));
      }
      var priceWrap = el.querySelector('.option__info-price .variant-price');
      if (priceWrap) el.dataset.birthdayPriceHtml = priceWrap.innerHTML;
    });
  }

  function paintLegacyOptionPrices(optionEl, percent, active) {
    var baseTotal = parseMoney(optionEl.dataset.birthdayBaseTotal);
    var baseDay = parseMoney(optionEl.dataset.birthdayBasePerDay);
    if (baseTotal == null) return;

    var priceWrap = optionEl.querySelector('.option__info-price .variant-price');
    if (!active || !percent) {
      if (priceWrap && optionEl.dataset.birthdayPriceHtml) {
        priceWrap.innerHTML = optionEl.dataset.birthdayPriceHtml;
      }
      return;
    }

    var baseCents = eurosToCents(baseTotal);
    var totalCents = discountCents(baseCents, percent);
    var dayCents = baseDay != null ? discountCents(eurosToCents(baseDay), percent) : null;
    var total = centsToEuros(totalCents);
    var day = dayCents != null ? centsToEuros(dayCents) : null;
    var listStrike = resolveListStrikeEuros(baseTotal, optionEl);

    var priceNodes = optionEl.querySelectorAll('[itemprop="price"]');
    priceNodes.forEach(function (node, idx) {
      var isTotal = idx === priceNodes.length - 1;
      var amountCents = isTotal ? totalCents : dayCents != null ? dayCents : totalCents;
      if (amountCents == null) return;
      node.setAttribute('content', String(amountCents));
      var suffixMatch = String(node.textContent || '').match(/\s*(\/.*)$/);
      var suffix = suffixMatch ? suffixMatch[1] : '';
      node.textContent = formatMoney(centsToEuros(amountCents)) + suffix;
    });

    optionEl.querySelectorAll('.variant-price .h5').forEach(function (h5) {
      if (h5.querySelector('span[style*="line-through"]')) {
        var spans = h5.querySelectorAll('span');
        if (spans.length > 1 && listStrike > total) {
          spans[0].textContent = formatMoney(listStrike);
        }
        if (spans.length) spans[spans.length - 1].textContent = ' ' + formatMoney(total);
        return;
      }
      if (h5.hasAttribute('data-nosnippet')) {
        if (day == null) return;
        var daySuffixMatch = String(h5.textContent || '').match(/(\/.*)$/);
        var daySuffix = daySuffixMatch ? daySuffixMatch[1] : '';
        h5.textContent = formatMoney(day) + daySuffix;
        return;
      }
      h5.textContent = formatMoney(total);
    });

    // Persist list strike so birthday card / ATC can read it from the option node.
    if (listStrike > total) {
      optionEl.dataset.strikePrice = formatMoney(listStrike);
    }
  }

  function percentForOption(content, cart, baseTotal, fallbackPercent, active, root) {
    if (!active || !fallbackPercent) return 0;
    if (content && content.discount_mode === 'tiered') {
      return resolvePercentForVariant(content, cart, baseTotal || 0, root);
    }
    return fallbackPercent;
  }

  function refreshV2PlanPickerBars(root) {
    var picker = productRoot(root).querySelector('plan-picker-v2-ab');
    if (!picker || typeof picker.updateInfoBar !== 'function') return;
    var type = picker.activeType || picker.dataset.currentType || 'subscription';
    var panel = picker.querySelector('.v2-panel[data-panel="' + type + '"]');
    var active = panel && panel.querySelector('.v2-option-pill.active');
    if (active) picker.updateInfoBar(active, type);
  }

  function refreshSelectorInfoBars(activePillNode, root) {
    var pill = activePillNode || activePill(root) || activePackageInfo(root);
    if (!pill) return;
    var ctx = productRoot(root);
    var prefix = isSubscription(root) ? 'sub' : 'ot';
    var priceEl = ctx.querySelector('#item_' + prefix + '_price');
    var totalEl = ctx.querySelector('#item_' + prefix + '_total');
    var strikeEl = ctx.querySelector('#item_' + prefix + '_strike_price');
    var dayLabel = (priceEl && priceEl.dataset.dayLabel) || '';
    var totalLabel = (totalEl && totalEl.dataset.totalLabel) || '';

    if (priceEl) {
      var dayText = pill.dataset.price;
      if (!dayText) {
        var baseDay = readBasePerDay(pill);
        if (baseDay != null) dayText = formatMoney(baseDay);
      }
      if (dayText) priceEl.textContent = dayLabel ? dayText + '/' + dayLabel : dayText;
    }

    if (totalEl) {
      var totalText = pill.dataset.priceDefault;
      if (!totalText) {
        var baseTotal = readBasePackagePrice(pill);
        if (baseTotal != null) totalText = formatMoney(baseTotal);
      }
      if (totalText) totalEl.textContent = totalLabel ? totalText + '/' + totalLabel : totalText;
    }

    if (strikeEl) {
      if (pill.dataset.strikePrice) {
        strikeEl.textContent = pill.dataset.strikePrice;
        strikeEl.style.display = '';
      } else {
        var basePkg = readBasePackagePrice(pill);
        var orange = orangePercent(pill);
        if (isSubscription(root) && basePkg != null && orange > 0) {
          strikeEl.textContent = formatMoney(subscriptionStrike(basePkg, orange));
          strikeEl.style.display = '';
        } else {
          strikeEl.style.display = 'none';
        }
      }
    }
  }

  function optionElHasStrike(pill) {
    return !!(
      pill &&
      (pill.dataset.strikePrice ||
        pill.querySelector('.sub-prices-item') ||
        pill.querySelector('.ot-prices-item') ||
        pill.querySelector('[data-strike-price]'))
    );
  }

  function applySelectorPrices(content, cart, percent, active, root) {
    var ctx = productRoot(root);
    rememberPillBases(root);
    rememberLegacyOptionBases(root);

    ctx.querySelectorAll(pickerPriceNodesSelector()).forEach(function (el) {
      var baseTotal = parseMoney(el.dataset.birthdayBaseTotal);
      var baseDay = parseMoney(el.dataset.birthdayBasePerDay);
      if (baseTotal == null) return;

      var optionPercent = percentForOption(content, cart, baseTotal, percent, active, root);

      if (active && optionPercent > 0) {
        var baseCents = eurosToCents(baseTotal);
        var saleCents = discountCents(baseCents, optionPercent);
        el.dataset.priceDefault = formatMoney(centsToEuros(saleCents));
        if (baseDay != null) {
          el.dataset.price = formatMoney(centsToEuros(discountCents(eurosToCents(baseDay), optionPercent)));
        }
        el.dataset.strikePrice = formatMoney(resolveListStrikeEuros(baseTotal, el));
      } else {
        el.dataset.priceDefault = el.dataset.birthdayBaseTotal;
        if (el.dataset.birthdayBasePerDay) el.dataset.price = el.dataset.birthdayBasePerDay;
        el.dataset.strikePrice = el.dataset.birthdayBaseStrike || '';
      }

      paintMenobacsCardPrices(el);
    });

    ctx.querySelectorAll('.option__info[data-birthday-base-total]').forEach(function (el) {
      var baseTotal = parseMoney(el.dataset.birthdayBaseTotal);
      if (baseTotal == null) return;
      var optionPercent = percentForOption(content, cart, baseTotal, percent, active, root);
      paintLegacyOptionPrices(el, optionPercent, active);
    });

    refreshSelectorInfoBars(null, root);
    refreshV2PlanPickerBars(root);
  }

  function applyCampaignTag(content, cart, percent, activeOverride, root) {
    content = content || getContent() || {};
    var infoMode = isInfoCardMode(content);
    var active = activeOverride != null ? !!activeOverride : !infoMode && isActive();
    if (infoMode) active = false;
    var hasConfiguredTag =
      !infoMode &&
      (!!(content.selector_tag_text && String(content.selector_tag_text).trim()) ||
        (content.selector_tag_percent != null && Number(content.selector_tag_percent) > 0));

    productRoot(root).querySelectorAll('[data-campaign-selector-tag]').forEach(function (tag) {
      if (!hasConfiguredTag) {
        tag.textContent = '';
        tag.classList.remove('is-birthday-active');
        tag.hidden = true;
        tag.setAttribute('hidden', '');
        tag.style.setProperty('display', 'none', 'important');
        return;
      }

      var optionNode = optionNodeFromTag(tag);
      var variantPrice = readOptionVariantPrice(optionNode);
      var tagPercent =
        content.discount_mode === 'tiered'
          ? resolvePercentForVariant(content, cart, variantPrice, root)
          : percent;
      if (!tagPercent) {
        tag.textContent = '';
        tag.classList.remove('is-birthday-active');
        tag.hidden = true;
        tag.setAttribute('hidden', '');
        tag.style.setProperty('display', 'none', 'important');
        return;
      }

      tag.textContent = formatSelectorTagText(tagPercent);
      if (!String(tag.textContent || '').trim()) {
        tag.hidden = true;
        tag.setAttribute('hidden', '');
        tag.style.setProperty('display', 'none', 'important');
        tag.classList.remove('is-birthday-active');
        return;
      }
      tag.hidden = false;
      tag.removeAttribute('hidden');
      tag.style.removeProperty('display');
      tag.classList.toggle('is-birthday-active', active);
    });
  }

  function isInfoCardMode(content) {
    content = content || getContent() || {};
    var mode = String(content.birthday_card_mode || '').toLowerCase();
    if (mode === 'info' || mode === 'popup') return true;
    var key = String((window.CampaignSegmentation && CampaignSegmentation.getSegment && CampaignSegmentation.getSegment() && CampaignSegmentation.getSegment().key) || '').toLowerCase();
    return key === 'influencer';
  }

  function showUpTo(content, percent) {
    content = content || {};
    if (isInfoCardMode(content)) return true;
    if (content.discount_mode === 'tiered') return true;
    var label = String(content.discount_label || content.product_badge_text || content.selector_tag_text || '').toLowerCase();
    return label.indexOf('bis zu') !== -1 || label.indexOf('up to') !== -1;
  }

  function infoNoteTemplate() {
    var card = document.querySelector('birthday-sale-card');
    var withCode =
      card && card.dataset.infoNoteWithCode
        ? card.dataset.infoNoteWithCode
        : 'Code {{ code }} im Warenkorb eingeben & Rabatt erhalten.';
    var noCode =
      card && card.dataset.infoNoteNoCode
        ? card.dataset.infoNoteNoCode
        : 'Code im Warenkorb eingeben. Rabatt wird direkt abgezogen';
    return { withCode: withCode, noCode: noCode };
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function defaultInfoNoteHtml(content) {
    var note = content && content.birthday_info_note ? String(content.birthday_info_note).trim() : '';
    var code = content && content.discount_code ? String(content.discount_code).trim() : '';
    var templates = infoNoteTemplate();

    if (note && note.indexOf('{{') === -1 && note.indexOf('<') === -1) {
      if (code && note.toLowerCase().indexOf(code.toLowerCase()) === -1) {
        return (
          'Code <strong>' +
          escapeHtml(code) +
          '</strong> im Warenkorb eingeben & Rabatt erhalten.'
        );
      }
      if (code) {
        return note.replace(
          new RegExp('(' + code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'i'),
          '<strong>$1</strong>'
        );
      }
      return escapeHtml(note);
    }

    if (code) {
      return templates.withCode.replace('{{ code }}', '<strong>' + escapeHtml(code) + '</strong>');
    }

    return escapeHtml(templates.noCode);
  }

  function buildDefaultInfoPopupBlocks() {
    return (
      '<div class="birthday-sale-card__popup-block">' +
      '<p class="birthday-sale-card__popup-headline">35% RABATT</p>' +
      '<p class="birthday-sale-card__popup-subline">für Neukund:innen</p>' +
      '</div>' +
      '<div class="birthday-sale-card__popup-block">' +
      '<p class="birthday-sale-card__popup-headline">30% RABATT</p>' +
      '<p class="birthday-sale-card__popup-subline">für Bestandskund:innen</p>' +
      '</div>'
    );
  }

  function normalizeInfoPopupHtml(raw) {
    if (raw.indexOf('birthday-sale-card__popup-block') !== -1) return raw;

    var blocks = [];
    var re = /<p[^>]*>([\s\S]*?)<\/p>/gi;
    var match;
    while ((match = re.exec(raw))) {
      var inner = match[1];
      var strongMatch = inner.match(/<strong[^>]*>([\s\S]*?)<\/strong>/i);
      var headline = strongMatch ? strongMatch[1].replace(/<[^>]+>/g, '').trim() : '';
      var subline = inner.replace(/<strong[^>]*>[\s\S]*?<\/strong>/i, '').replace(/<[^>]+>/g, '').trim();
      if (!headline) headline = inner.replace(/<[^>]+>/g, '').trim();
      if (headline) blocks.push({ headline: headline, subline: subline });
    }

    if (!blocks.length) return buildDefaultInfoPopupBlocks();

    return blocks
      .map(function (block) {
        return (
          '<div class="birthday-sale-card__popup-block">' +
          '<p class="birthday-sale-card__popup-headline">' +
          escapeHtml(block.headline) +
          '</p>' +
          (block.subline
            ? '<p class="birthday-sale-card__popup-subline">' + escapeHtml(block.subline) + '</p>'
            : '') +
          '</div>'
        );
      })
      .join('');
  }

  function defaultInfoPopupHtml(content) {
    if (content && content.birthday_info_popup && String(content.birthday_info_popup).trim()) {
      return normalizeInfoPopupHtml(String(content.birthday_info_popup).trim());
    }
    return buildDefaultInfoPopupBlocks();
  }

  function closeInfoPopup(card) {
    var popup = card.querySelector('[data-birthday-info-popup]');
    var btn = card.querySelector('[data-birthday-info-btn]');
    if (popup) {
      popup.hidden = true;
      popup.setAttribute('hidden', '');
    }
    if (btn) btn.setAttribute('aria-expanded', 'false');
  }

  function toggleInfoPopup(card, forceOpen) {
    var popup = card.querySelector('[data-birthday-info-popup]');
    var btn = card.querySelector('[data-birthday-info-btn]');
    if (!popup) return;
    var open = forceOpen != null ? forceOpen : popup.hasAttribute('hidden');
    if (open) {
      popup.hidden = false;
      popup.removeAttribute('hidden');
      if (btn) btn.setAttribute('aria-expanded', 'true');
    } else {
      closeInfoPopup(card);
    }
  }

  function ensureAtcBirthdayBase(main, baseCents) {
    if (!main || !main.dataset || !baseCents) return;
    var key = isSubscription(main) ? 'birthdayBaseSubscriptionPrice' : 'birthdayBaseOnetimerPrice';
    if (!main.dataset[key]) {
      main.dataset[key] = formatMoney(centsToEuros(baseCents));
    }
  }

  function readStoredAtcBaseCents(main) {
    if (!main || !main.dataset) return null;
    var keys = isSubscription(main)
      ? ['birthdayBaseSubscriptionPrice', 'campaignBaseSubscriptionPrice', 'subscriptionPrice']
      : ['birthdayBaseOnetimerPrice', 'campaignBaseOnetimerPrice', 'onetimerPrice'];
    for (var i = 0; i < keys.length; i++) {
      var euros = parseMoney(main.dataset[keys[i]]);
      if (euros != null && euros > 0) return eurosToCents(euros);
    }
    return null;
  }

  function resolveAtcBaseCents(main, totals, root) {
    var fromVariant = readSelectedVariantPriceCents(root || main);
    if (fromVariant != null && fromVariant > 0) return fromVariant;

    var fromMain = main && readStoredAtcBaseCents(main);
    if (fromMain != null && fromMain > 0) return fromMain;

    var fromTotals = totals && (totals.packageCents || eurosToCents(totals.packagePrice));
    if (fromTotals != null && fromTotals > 0) return fromTotals;

    return readAtcMainPriceCents(root || main);
  }

  function isSimpleFormAtc(main) {
    return !!(main && main.dataset && (main.dataset.onetimerPrice || main.dataset.subscriptionPrice));
  }

  function isBirthdayDiscountActive(requestedActive) {
    if (isInfoCardMode(getContent() || {})) return false;
    if (requestedActive) return true;
    return isActive() || document.documentElement.hasAttribute('data-birthday-sale-active');
  }

  function findAtcPriceWraps(root) {
    var wraps = [];
    var seen = [];

    function add(el) {
      if (!el || seen.indexOf(el) !== -1) return;
      seen.push(el);
      wraps.push(el);
    }

    var ctx = productRoot(root);
    ctx.querySelectorAll('.product-form__submit .selected-variant-price').forEach(add);

    var info =
      (ctx.matches && ctx.matches('product-info') && ctx) ||
      ctx.querySelector('product-info');
    var sectionId = info && info.dataset && info.dataset.section;
    if (sectionId) {
      var submit = document.getElementById('ProductSubmitButton-' + sectionId);
      if (submit && ctx.contains(submit)) submit.querySelectorAll('.selected-variant-price').forEach(add);
    }

    ctx.querySelectorAll('.selected-variant-price').forEach(function (el) {
      if (el.closest('.product-form__submit')) add(el);
    });

    if (!isQuickAddRoot(ctx)) {
      document.querySelectorAll('.product-form__buttons.sticky .selected-variant-price').forEach(add);
    }

    return wraps;
  }

  function syncStickyAtcPortalFromOriginal(root) {
    if (isQuickAddRoot(root)) return;
    var originalWrap = document.querySelector(
      'product-form .product-form__buttons:not(.sticky) .product-form__submit .selected-variant-price'
    );
    var stickyWrap = document.querySelector(
      '.product-form__buttons.sticky .product-form__submit .selected-variant-price'
    );
    if (originalWrap && stickyWrap) stickyWrap.innerHTML = originalWrap.innerHTML;
  }

  function paintAtcSaleElement(sale, strike, showStrike) {
    if (!sale) return;
    if (showStrike) {
      sale.textContent = formatCardPrice(strike);
      sale.hidden = false;
      sale.removeAttribute('hidden');
      sale.setAttribute('data-birthday-atc-strike', '1');
      sale.style.setProperty('display', 'inline', 'important');
      sale.style.setProperty('color', '#fff', 'important');
      sale.style.setProperty('text-decoration', 'line-through', 'important');
      sale.style.textDecorationThickness = '1px';
      sale.style.opacity = '0.5';
    } else {
      sale.textContent = '';
      sale.removeAttribute('data-birthday-atc-strike');
      sale.style.setProperty('display', 'none', 'important');
    }
  }

  function paintAtcPriceWrap(wrap, current, strike, showStrike, active, percent, skipDatasetSync) {
    if (!wrap) return null;
    var main = wrap.querySelector('.atc-price_main');
    if (!main) return null;

    var useSimpleLayout = isSimpleFormAtc(main) || wrap.classList.contains('selected-variant-price');
    var nextMain = main;

    if (useSimpleLayout) {
      nextMain = paintSimpleAtcWrap(wrap, main, current, strike, showStrike);
      if (nextMain && !skipDatasetSync) syncProductFormPriceDatasets(nextMain, active, percent);
    } else {
      if (!wrap.querySelector('.atc-price_sale') && nextMain) {
        var saleNode = document.createElement('span');
        saleNode.className = 'atc-price_sale caption';
        wrap.insertBefore(saleNode, nextMain);
      }
      nextMain.textContent = active && percent > 0 ? formatCardPrice(current) : formatMoney(current);
      paintAtcSaleElement(wrap.querySelector('.atc-price_sale'), strike, showStrike);
    }

    return nextMain;
  }

  function syncProductFormPriceDatasets(main, active, percent) {
    if (!main || !main.dataset) return false;
    if (!main.dataset.subscriptionPrice && !main.dataset.onetimerPrice) return false;

    ['subscriptionPrice', 'onetimerPrice'].forEach(function (key) {
      var raw = main.dataset[key];
      if (!raw) return;
      var baseKey = 'birthdayBase' + key.charAt(0).toUpperCase() + key.slice(1);
      if (!main.dataset[baseKey]) main.dataset[baseKey] = raw;
      var baseCents = eurosToCents(parseMoney(main.dataset[baseKey]));
      if (!baseCents) return;
      var nextCents = active && percent > 0 ? discountCents(baseCents, percent) : baseCents;
      main.dataset[key] = formatMoney(centsToEuros(nextCents));
    });

    var productForm = main.closest('product-form') || productRoot(main).querySelector('product-form');
    var sellingPlan = productForm && productForm.querySelector('input[name="selling_plan"]');
    if (productForm && sellingPlan && typeof productForm.togglePrices === 'function') {
      productForm.togglePrices();
    }
    return true;
  }

  function alpinePriceScopes(root) {
    return productRoot(root).querySelectorAll('.product__buy-buttons [x-data], product-form [x-data]');
  }

  function applyCtaPrices(percent, active, totals, root) {
    totals = totals || selectedTotals(root);
    var activeNow = isBirthdayDiscountActive(active);
    var wraps = findAtcPriceWraps(root);
    var sampleMain = (wraps[0] && wraps[0].querySelector('.atc-price_main')) || atcPriceNodes(root).main;
    var picker = readPickerDisplayedTotals(root);

    if (picker.sale != null && picker.sale > 0) {
      var pickerCurrent = picker.sale;
      var pickerStrike = picker.strike && picker.strike > picker.sale ? picker.strike : picker.sale;
      var pickerShowStrike = pickerStrike > pickerCurrent;
      var pickerPriceCents = eurosToCents(pickerCurrent);
      var pickerCompareCents = pickerShowStrike ? eurosToCents(pickerStrike) : 0;

      if (sampleMain && sampleMain.dataset) {
        if (isSubscription(root) && sampleMain.dataset.subscriptionPrice) {
          sampleMain.dataset.subscriptionPrice = formatMoney(pickerCurrent);
        }
        if (!isSubscription(root) && sampleMain.dataset.onetimerPrice) {
          sampleMain.dataset.onetimerPrice = formatMoney(pickerCurrent);
        }
      }

      function paintPickerAtc() {
        var live = readPickerDisplayedTotals(root);
        if (live.sale == null || live.sale <= 0) return;

        var liveCurrent = live.sale;
        var livePill = live.pill || (totals && totals.pill) || selectedOptionNode(root);
        var preBirthday =
          parseMoney(livePill && livePill.dataset && livePill.dataset.birthdayBaseTotal) || liveCurrent;
        var liveStrike = resolveListStrikeEuros(preBirthday, livePill);
        if (!(liveStrike > liveCurrent)) liveStrike = liveCurrent;
        var liveShowStrike = liveStrike > liveCurrent;
        var livePriceCents = eurosToCents(liveCurrent);
        var liveCompareCents = liveShowStrike ? eurosToCents(liveStrike) : 0;

        paintingAtc = true;
        wraps = findAtcPriceWraps(root);
        wraps.forEach(function (wrap) {
          paintAtcPriceWrap(wrap, liveCurrent, liveStrike, liveShowStrike, true, percent || 1, true);
        });
        syncStickyAtcPortalFromOriginal(root);
        alpinePriceScopes(root).forEach(function (el) {
          var data = null;
          if (window.Alpine && typeof Alpine.$data === 'function') {
            try {
              data = Alpine.$data(el);
            } catch (e) {}
          }
          if (!data && el._x_dataStack && el._x_dataStack[0]) data = el._x_dataStack[0];
          if (!data || typeof data.price === 'undefined') return;
          data.comparePrice = liveCompareCents;
          data.price = livePriceCents;
        });
        window.setTimeout(function () {
          paintingAtc = false;
        }, 0);
      }

      paintPickerAtc();
      requestAnimationFrame(paintPickerAtc);
      [80, 200, 400].forEach(function (delay) {
        window.setTimeout(paintPickerAtc, delay);
      });
      return;
    }

    var baseCents = resolveAtcBaseCents(sampleMain, totals, root);
    if (!baseCents) return;

    if (sampleMain) ensureAtcBirthdayBase(sampleMain, baseCents);

    var pkgCents = baseCents;
    var pkg = centsToEuros(pkgCents);
    var orange = totals.orange || 0;
    var listStrike = 0;
    if (isSubscription(root)) {
      listStrike = orange > 0 ? subscriptionStrike(pkg, orange) : 0;
    }
    if (!listStrike || listStrike <= pkg) {
      var fromOption = readOptionStrike(totals.pill);
      if (fromOption && fromOption > pkg) listStrike = fromOption;
    }
    if (!listStrike || listStrike <= pkg) {
      listStrike = readBundledListStrike(totals.pill) || 0;
    }
    if (!listStrike || listStrike <= pkg) {
      var prefix = isSubscription(root) ? 'sub' : 'ot';
      var barStrike = productRoot(root).querySelector('#item_' + prefix + '_strike_price');
      if (barStrike && barStrike.style.display !== 'none') {
        var barAmt = parseMoney(barStrike.textContent);
        if (barAmt && barAmt > pkg) listStrike = barAmt;
      }
    }
    if (!listStrike || listStrike <= pkg) {
      var atcCompare = readAtcCompareStrike(root);
      if (atcCompare && atcCompare > pkg) listStrike = atcCompare;
    }

    if (fallSalePreSaleStrike()) listStrike = pkg;

    var strikeCents = pkgCents;
    var currentCents = pkgCents;

    if (activeNow && percent > 0) {
      currentCents = discountCents(pkgCents, percent);
    } else {
      currentCents = pkgCents;
    }

    var listStrikeCents = listStrike > centsToEuros(currentCents) ? eurosToCents(listStrike) : 0;
    strikeCents = listStrikeCents > currentCents ? listStrikeCents : 0;

    var current = centsToEuros(currentCents);
    var strike = centsToEuros(strikeCents);
    var showStrike = strikeCents > currentCents;
    var compareCents = showStrike ? strikeCents : 0;
    var priceCents = currentCents;

    function paintAll() {
      paintingAtc = true;
      wraps = findAtcPriceWraps(root);
      wraps.forEach(function (wrap) {
        var mainInWrap = wrap.querySelector('.atc-price_main');
        if (mainInWrap) ensureAtcBirthdayBase(mainInWrap, baseCents);
        paintAtcPriceWrap(wrap, current, strike, showStrike, activeNow, percent);
      });
      syncStickyAtcPortalFromOriginal(root);

      alpinePriceScopes(root).forEach(function (el) {
        var data = null;
        if (window.Alpine && typeof Alpine.$data === 'function') {
          try {
            data = Alpine.$data(el);
          } catch (e) {}
        }
        if (!data && el._x_dataStack && el._x_dataStack[0]) data = el._x_dataStack[0];
        if (!data || typeof data.price === 'undefined') return;
        data.comparePrice = compareCents;
        data.price = priceCents;
      });
      window.setTimeout(function () {
        paintingAtc = false;
      }, 0);
    }

    paintAll();
    requestAnimationFrame(paintAll);

    if (showStrike) {
      [120, 350, 650, 1000].forEach(function (delay) {
        window.setTimeout(paintAll, delay);
      });
    }
  }

  function readBundledListStrike(pill) {
    if (!pill) return null;
    // price_without_bundle (and similar) rendered as an inline strikethrough under the title.
    var nodes = pill.querySelectorAll('.option__info-name [style*="line-through"], .option__info-name s, .option__info-name del');
    for (var i = 0; i < nodes.length; i++) {
      var amount = parseMoney(nodes[i].textContent);
      if (amount && amount > 0) return amount;
    }
    return null;
  }

  function readOptionStrike(pill) {
    if (!pill) return null;
    var el =
      pill.querySelector('.sub-prices-item') ||
      pill.querySelector('.ot-prices-item') ||
      pill.querySelector('[data-strike-price]') ||
      null;
    if (el && el.dataset && el.dataset.strikePrice) {
      return parseMoney(el.dataset.strikePrice);
    }
    if (el) return parseMoney(el.textContent);
    if (pill.dataset && pill.dataset.strikePrice) {
      return parseMoney(pill.dataset.strikePrice);
    }
    return null;
  }

  function highlightCartCode(code) {
    if (!code) return;
    document.querySelectorAll('.discounts__discount .code-name, .discounts__discount [class*="code"]').forEach(function (el) {
      if (el.textContent && el.textContent.trim().toUpperCase() === String(code).toUpperCase()) {
        var chip = el.closest('.discounts__discount');
        if (chip) chip.classList.add('is-birthday-sale');
      }
    });
  }

  function resolveCartDiscountCode(content, cart, packagePrice, root) {
    content = content || getContent() || {};
    var code = content.discount_code || '';
    if (!code || content.discount_mode !== 'tiered') return code;

    var lowCode = content.tier_discount_code_low || '';
    var highCode = content.tier_discount_code_high || '';
    if (!lowCode && !highCode) return code;

    var basket = projectedBasketForVariant(cart, packagePrice || 0, root);
    var percent = resolvePercent(content, basket);
    var high = parseFloat(content.tier_percent_high) || 30;
    if (percent >= high && highCode) return highCode;
    if (lowCode) return lowCode;
    return code;
  }

  function applyCodeForActiveState(cart, packagePrice, root) {
    var content = getContent() || {};
    if (isCodeDismissed()) return Promise.resolve();
    if (!content.discount_code || isInfoCardMode(content) || !isActive()) return Promise.resolve();
    // AIOD tiered codes need line items in cart — applying to an empty cart sticks in a stale
    // state and blocks the first real apply via the _pending dedupe guard.
    if (!cart || !cart.item_count) return Promise.resolve();
    // Never overwrite an influencer / manual code the customer already applied.
    if (cartHasForeignDiscountCode(cart)) {
      dismissBirthdayCode();
      return Promise.resolve();
    }
    return applyCodeToCart(resolveCartDiscountCode(content, cart, packagePrice, root));
  }

  function applyCodeAfterCartAdd(fromEl) {
    if (isCodeDismissed() || !isActive() || isInfoCardMode()) return Promise.resolve();
    document.documentElement.setAttribute('data-birthday-applying-code', '1');
    return fetchCart()
      .then(function (cart) {
        var totals = selectedTotals(fromEl);
        return applyCodeForActiveState(cart, totals.packagePrice, fromEl);
      })
      .finally(function () {
        // Cleared in finish() after refresh; keep as safety if apply no-ops.
        window.setTimeout(function () {
          if (!applyCodeToCart._pending) {
            document.documentElement.removeAttribute('data-birthday-applying-code');
          }
        }, 1200);
      });
  }

  function cartHasDiscountCode(cart, code) {
    if (!cart || !code) return false;
    var normalized = normalizeCode(code);
    if ((cart.discount_codes || []).some(function (entry) {
      var value = typeof entry === 'string' ? entry : entry && entry.code;
      return normalizeCode(value) === normalized;
    })) {
      return true;
    }
    return !!(cart.total_discount && cart.total_discount > 0);
  }

  function refreshDrawerAfterDiscount(code) {
    var attempts = 0;
    var maxAttempts = 6;

    var tick = function () {
      attempts += 1;
      return fetchCart().then(function (cart) {
        var ready = cartHasDiscountCode(cart, code) || attempts >= maxAttempts;
        if (!ready) {
          return new Promise(function (resolve) {
            window.setTimeout(resolve, 120);
          }).then(tick);
        }

        highlightCartCode(code);
        var drawer = document.querySelector('cart-drawer');
        var openDrawer = !!(drawer && drawer.classList.contains('active'));
        var refreshPromise = Promise.resolve();
        if (window.CartItems && typeof CartItems.refreshCartDrawerSections === 'function') {
          refreshPromise = CartItems.refreshCartDrawerSections({ openDrawer: openDrawer }) || refreshPromise;
        } else if (window.CartItems && typeof CartItems.cartDrawerRenderContent === 'function') {
          refreshPromise = CartItems.cartDrawerRenderContent() || refreshPromise;
        }
        return Promise.resolve(refreshPromise).finally(function () {
          applyCodeToCart._pending = null;
          document.documentElement.removeAttribute('data-birthday-applying-code');
        });
      });
    };

    return tick();
  }

  function applyCodeToCart(code) {
    if (!code) return Promise.resolve();
    if (isCodeDismissed() || !isActive()) return Promise.resolve();

    var scheduledGen = applyCodeToCart._gen || 0;
    document.documentElement.setAttribute('data-birthday-applying-code', '1');

    var work = function () {
      // User may have dismissed while this apply was queued.
      if (
        isCodeDismissed() ||
        !isActive() ||
        scheduledGen !== (applyCodeToCart._gen || 0)
      ) {
        applyCodeToCart._pending = null;
        document.documentElement.removeAttribute('data-birthday-applying-code');
        return Promise.resolve();
      }
      applyCodeToCart._pending = code;

      var applyPromise;
      if (window.CartItems && typeof CartItems.applyDiscountCodesToCart === 'function') {
        applyPromise = CartItems.applyDiscountCodesToCart([code]);
      } else {
        var component = document.querySelector('m-discount-codes-component');
        if (component && typeof component.applyDiscount === 'function') {
          applyPromise = component.applyDiscount([code]);
        } else {
          applyCodeToCart._pending = null;
          document.documentElement.removeAttribute('data-birthday-applying-code');
          return Promise.resolve();
        }
      }

      return applyPromise
        .then(function () {
          if (
            isCodeDismissed() ||
            !isActive() ||
            scheduledGen !== (applyCodeToCart._gen || 0)
          ) {
            applyCodeToCart._pending = null;
            document.documentElement.removeAttribute('data-birthday-applying-code');
            return;
          }
          if (typeof publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'birthday-sale-discount' });
          }
          // One drawer refresh after AIOD/cart has the code — avoids chip flash.
          return refreshDrawerAfterDiscount(code);
        })
        .catch(function () {
          applyCodeToCart._pending = null;
          document.documentElement.removeAttribute('data-birthday-applying-code');
        });
    };

    applyCodeToCart._chain = (applyCodeToCart._chain || Promise.resolve()).then(work, work);
    return applyCodeToCart._chain;
  }

  function syncUI(card) {
    var content = getContent();
    if (!content || (!content.discount_code && !content.discount_percent && content.discount_mode !== 'tiered')) {
      card.hidden = true;
      return;
    }

    var root = productRoot(card);
    var totals = selectedTotals(root);
    // Fall Sale: flat % and no discount code, so the cart is not needed — paint in the same
    // frame instead of waiting for /cart.js (avoids the price flashing on plan switch).
    var cartReady = fallSalePreSaleStrike()
      ? Promise.resolve({ items: [], item_count: 0, total_price: 0 })
      : fetchCart();
    cartReady.then(function (cart) {
      var basket = projectedBasketEuros(cart, totals.packagePrice, root);
      var percent = resolvePercent(content, basket);
      var cardPercent = displayPercentForCard(content, percent);
      var infoMode = isInfoCardMode(content);
      // Info segments never use the Birthday toggle / purple selector fill.
      var active = infoMode ? false : isActive();
      var code = content.discount_code || '';

      card.hidden = false;
      card.classList.toggle('is-info', infoMode);
      card.classList.toggle('is-active', !infoMode && active);
      card.classList.toggle('is-toggle', !infoMode);

      var toggle = card.querySelector('[data-birthday-toggle]');
      var infoBtn = card.querySelector('[data-birthday-info-btn]');
      var infoNote = card.querySelector('[data-birthday-info-note]');
      var infoBody = card.querySelector('[data-birthday-info-popup-body]');
      var pricesWrap = card.querySelector('[data-birthday-prices]');
      var cta = card.querySelector('[data-birthday-cta]');

      if (toggle) {
        toggle.hidden = infoMode;
        if (infoMode) toggle.setAttribute('hidden', '');
        else toggle.removeAttribute('hidden');
        toggle.setAttribute('aria-checked', active ? 'true' : 'false');
        toggle.setAttribute('aria-pressed', active ? 'true' : 'false');
      }

      if (infoBtn) {
        infoBtn.hidden = !infoMode;
        if (infoMode) infoBtn.removeAttribute('hidden');
        else infoBtn.setAttribute('hidden', '');
      }

      if (infoNote) {
        if (infoMode) {
          infoNote.innerHTML = defaultInfoNoteHtml(content);
          infoNote.hidden = false;
          infoNote.removeAttribute('hidden');
        } else {
          infoNote.innerHTML = '';
          infoNote.hidden = true;
        }
      }

      if (infoBody && infoMode) {
        infoBody.innerHTML = defaultInfoPopupHtml(content);
      }

      if (cta) cta.hidden = infoMode;

      var percentValueEl = card.querySelector('[data-birthday-percent-value]');
      if (percentValueEl) percentValueEl.textContent = cardPercent ? String(cardPercent) : '';

      var uptoLabelEl = card.querySelector('.birthday-sale-card__upto-label');
      if (uptoLabelEl) uptoLabelEl.hidden = !showUpTo(content, cardPercent);

      var codeEl = card.querySelector('[data-birthday-code]');
      if (codeEl) {
        if (!infoMode && code) {
          codeEl.innerHTML = 'CODE: <strong></strong>';
          codeEl.querySelector('strong').textContent = code;
          codeEl.hidden = false;
          codeEl.removeAttribute('hidden');
        } else {
          codeEl.textContent = '';
          codeEl.hidden = true;
        }
      }

      var idle = card.querySelector('[data-birthday-cta-idle]');
      var on = card.querySelector('[data-birthday-cta-on]');
      if (idle) idle.classList.toggle('hidden', active || infoMode);
      if (on) on.classList.toggle('hidden', !active || infoMode);

      stripSaveWord(root);
      if (!infoMode) setActive(active);
      applyCampaignTag(content, cart, percent, active, root);
      if (!infoMode) applySelectorPrices(content, cart, percent, active, root);
      paintBirthdayCardPrices(card, content, cart, percent, active, infoMode, totals);
      applyCtaPrices(percent, active, totals, root);
      if (!infoMode) {
        highlightCartCode(resolveCartDiscountCode(content, cart, totals.packagePrice, root));
        // Re-apply only when still active AND not dismissed; foreign codes dismiss first.
        if (active && !isCodeDismissed()) applyCodeForActiveState(cart, totals.packagePrice, root);
      } else {
        // Keep session activation for toggle PDPs; only suppress active UI here.
        document.documentElement.removeAttribute('data-birthday-sale-active');
      }

      document.documentElement.dataset.birthdaySalePercent = String(percent);
      document.documentElement.toggleAttribute('data-birthday-card-info', infoMode);
      document.dispatchEvent(
        new CustomEvent('birthday-sale:updated', {
          detail: { active: active, percent: percent, code: code, basket: basket, infoMode: infoMode },
        })
      );
    });
  }

  function syncAllCards() {
    document.querySelectorAll('birthday-sale-card').forEach(function (card) {
      syncUI(card);
    });
  }

  if (!customElements.get('birthday-sale-card')) {
    customElements.define(
      'birthday-sale-card',
      class BirthdaySaleCard extends HTMLElement {
        connectedCallback() {
          var self = this;
          this._onToggle = function (event) {
            if (isInfoCardMode()) return;
            event.preventDefault();
            event.stopPropagation();
            setActive(!isActive());
            syncUI(self);
            fetchCart().then(function (cart) {
              var totals = selectedTotals(self);
              if (isActive()) applyCodeForActiveState(cart, totals.packagePrice, self);
            });
          };
          this._toggleEls = this.querySelectorAll('[data-birthday-toggle], [data-birthday-cta]');
          this._toggleEls.forEach(function (el) {
            el.addEventListener('click', self._onToggle);
          });
          var inner = this.querySelector('.birthday-sale-card__inner');
          this._onInnerClick = function (event) {
            if (isInfoCardMode()) {
              if (
                event.target.closest('[data-birthday-info-btn]') ||
                event.target.closest('[data-birthday-info-popup]')
              ) {
                return;
              }
              event.preventDefault();
              event.stopPropagation();
              return;
            }
            self._onToggle(event);
          };
          if (inner) inner.addEventListener('click', this._onInnerClick);

          this._onInfoBtn = function (event) {
            event.preventDefault();
            event.stopPropagation();
            toggleInfoPopup(self);
          };
          this._onInfoClose = function (event) {
            event.preventDefault();
            event.stopPropagation();
            closeInfoPopup(self);
          };
          var infoBtn = this.querySelector('[data-birthday-info-btn]');
          var infoClose = this.querySelector('[data-birthday-info-close]');
          if (infoBtn) infoBtn.addEventListener('click', this._onInfoBtn);
          if (infoClose) infoClose.addEventListener('click', this._onInfoClose);

          this._onDocClick = function (event) {
            if (!self.classList.contains('is-info')) return;
            if (event.target.closest('birthday-sale-card') === self) return;
            closeInfoPopup(self);
          };
          document.addEventListener('click', this._onDocClick);

          var boot = function () {
            maybeAutoActivateBirthday(getContent() || {});
            if (isActive() && !isInfoCardMode()) setActive(true);
            syncUI(self);
            if (isActive() && !isInfoCardMode()) {
              fetchCart().then(function (cart) {
                applyCodeForActiveState(cart, selectedTotals(self).packagePrice, self);
              });
            }
          };

          if (window.CampaignSegmentation && CampaignSegmentation.isReady()) boot();
          else document.addEventListener('campaign:segment-resolved', boot, { once: true });

          watchAtcButtonForResync(self);

          // ATC Alpine scope may init after the first sync — re-apply strike once it's ready.
          this._onAlpine = function () {
            window.setTimeout(function () {
              syncUI(self);
            }, 0);
          };
          document.addEventListener('alpine:initialized', this._onAlpine, { once: true });
          // Fallback if alpine:initialized already fired before this element connected.
          window.setTimeout(function () {
            syncUI(self);
          }, 300);

          this._onCampaignPrices = function () {
            window.setTimeout(function () {
              syncUI(self);
            }, 0);
          };
          document.addEventListener('campaign:prices-updated', this._onCampaignPrices);

          this._onPicker = function (event) {
            var ctx = productRoot(self);
            if (ctx && ctx !== document && !ctx.contains(event.target)) return;
            if (
              !event.target.closest('.v2-option-pill') &&
              !event.target.closest('.dropdown-option') &&
              !event.target.closest('.option__info') &&
              !event.target.closest('.v2-toggle-btn') &&
              !event.target.closest('.plan-button') &&
              !event.target.closest('.september-card') &&
              !event.target.closest('.menobacs-card') &&
              !(event.target.matches && event.target.matches('.product-options_package input[type="radio"]')) &&
              !event.target.closest('.product-options_package label') &&
              !(event.target.matches && event.target.matches('.swatch-input__input')) &&
              !event.target.closest('.swatch-input__label')
            ) {
              return;
            }
            window.setTimeout(function () {
              syncUI(self);
            }, 0);
            window.setTimeout(function () {
              syncUI(self);
            }, 80);
          };
          document.addEventListener('click', this._onPicker);
          this._onRadioChange = function (event) {
            var ctx = productRoot(self);
            if (ctx && ctx !== document && event && event.target && !ctx.contains(event.target)) return;
            window.setTimeout(function () {
              syncUI(self);
            }, 0);
          };
          document.addEventListener('change', this._onRadioChange);
          if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
            if (PUB_SUB_EVENTS.variantChange) {
              this._unsubVariant = subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
                if (!variantEventMatchesCard(event, self)) return;
                resetLegacyOptionBirthdayCache(self);
                [0, 100, 300, 650].forEach(function (delay) {
                  window.setTimeout(function () {
                    syncUI(self);
                  }, delay);
                });
              });
            }
            if (PUB_SUB_EVENTS.optionValueSelectionChange) {
              this._unsubOption = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, function (event) {
                if (!variantEventMatchesCard(event, self)) return;
                resetLegacyOptionBirthdayCache(self);
                [0, 100, 300, 650].forEach(function (delay) {
                  window.setTimeout(function () {
                    syncUI(self);
                  }, delay);
                });
              });
            }
            this._unsubCart = subscribe(PUB_SUB_EVENTS.cartUpdate, function (payload) {
              if (payload && payload.source === 'birthday-sale-discount') return;
              if (payload && payload.source === 'discount-code-applied') return;
              // ATC already triggers applyCodeAfterCartAdd — don't queue a second apply.
              if (payload && payload.source === 'product-form' && isActive()) {
                window.setTimeout(function () {
                  syncUI(self);
                }, 500);
                return;
              }
              if (applyCodeToCart._pending) return;
              window.setTimeout(function () {
                if (isCodeDismissed()) {
                  if (isActive()) setActive(false);
                  syncUI(self);
                  return;
                }
                syncUI(self);
                if (!isInfoCardMode()) {
                  fetchCart().then(function (cart) {
                    var totals = selectedTotals(self);
                    if (isActive() && !isCodeDismissed() && !applyCodeToCart._pending) {
                      applyCodeForActiveState(cart, totals.packagePrice, self);
                    }
                  });
                }
              }, 400);
            });
          }
        }

        disconnectedCallback() {
          var self = this;
          if (this._onToggle) {
            if (this._toggleEls) {
              this._toggleEls.forEach(function (el) {
                el.removeEventListener('click', self._onToggle);
              });
            }
            var inner = this.querySelector('.birthday-sale-card__inner');
            if (inner && this._onInnerClick) inner.removeEventListener('click', this._onInnerClick);
          }
          var infoBtn = this.querySelector('[data-birthday-info-btn]');
          var infoClose = this.querySelector('[data-birthday-info-close]');
          if (infoBtn && this._onInfoBtn) infoBtn.removeEventListener('click', this._onInfoBtn);
          if (infoClose && this._onInfoClose) infoClose.removeEventListener('click', this._onInfoClose);
          if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
          if (this._onPicker) document.removeEventListener('click', this._onPicker);
          if (this._onCampaignPrices) document.removeEventListener('campaign:prices-updated', this._onCampaignPrices);
          if (this._onRadioChange) document.removeEventListener('change', this._onRadioChange);
          if (this._onAlpine) document.removeEventListener('alpine:initialized', this._onAlpine);
          if (this._unsubVariant) this._unsubVariant();
          if (this._unsubOption) this._unsubOption();
          if (this._unsubCart) this._unsubCart();
        }
      }
    );
  }

  var paintingAtc = false;

  function reapplyAtcIfActive(root) {
    if (paintingAtc) return;
    var content = getContent() || {};
    if (!content.discount_code && !content.discount_percent && content.discount_mode !== 'tiered') return;
    var totals = selectedTotals(root);
    if (isInfoCardMode(content) || readPickerDisplayedTotals(root).sale != null) {
      applyCtaPrices(parseFloat(content.discount_percent) || 0, isActive(), totals, root);
      return;
    }
    if (!isActive()) return;
    fetchCart().then(function (cart) {
      var percent = resolvePercent(content, projectedBasketEuros(cart, totals.packagePrice, root));
      applyCtaPrices(percent, true, totals, root);
    });
  }

  function watchAtcButtonForResync(root) {
    var ctx = productRoot(root);
    var submit = ctx.querySelector('product-form [type="submit"]') || ctx.querySelector('[id^="ProductSubmitButton-"]');
    if (!submit || submit._birthdayAtcWatch) return;
    submit._birthdayAtcWatch = true;

    var timer = null;
    var observer = new MutationObserver(function () {
      if (paintingAtc) return;
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(function () {
        reapplyAtcIfActive(ctx);
      }, 60);
    });
    observer.observe(submit, { childList: true, subtree: true, characterData: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchAtcButtonForResync);
  } else {
    watchAtcButtonForResync();
  }

  window.BirthdaySalePDP = {
    isActive: isActive,
    setActive: setActive,
    dismissCode: dismissBirthdayCode,
    handleCartCodeRemoved: handleCartCodeRemoved,
    handleCartCodeAdded: handleCartCodeAdded,
    isSegmentDiscountCode: isSegmentDiscountCode,
    isCodeDismissed: isCodeDismissed,
    applyCodeToCart: applyCodeToCart,
    syncAll: syncAllCards,
    isInfoMode: isInfoCardMode,
    maybeAutoActivateBirthday: maybeAutoActivateBirthday,
    maybeAutoActivateFromUrl: maybeAutoActivateFromUrl,
    readUrlDiscountCode: readUrlDiscountCode,
    getCode: function () {
      var content = getContent() || {};
      return content.discount_code || '';
    },
    resolveCartDiscountCode: resolveCartDiscountCode,
    applyCodeForActiveState: applyCodeForActiveState,
    applyCodeAfterCartAdd: applyCodeAfterCartAdd,
    readPickerTotals: readPickerDisplayedTotals,
  };

  document.addEventListener('campaign:segment-resolved', function () {
    if (maybeAutoActivateBirthday(getContent() || {})) finishAutoActivate();
  });
})();
